// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Browser } from "@capacitor/browser";
import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { FullScreenDialog, useToast } from "@src/appV2/lib";
import { useHelloSignEmbeddedFlow } from "@src/appV2/redesign/Documents/Details/Workflows/HelloSign/useHelloSignEmbeddedFlow";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { PageWrapper } from "../../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../../components/StickyScrollPageHeader";
import { invalidateWorkerRequirements } from "../../../api/invalidateWorkerRequirements";
import { useGetRequirements } from "../../../api/useGetRequirements";
import { DocumentsErrorPage } from "../../../ErrorPage";
import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_ERROR_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
} from "../../../paths";
import { DocumentSubmissionInProgressBottomSheet } from "../../../Submission/InProgressBottomSheet";
import { DocumentUploadFooter } from "../../Footer";
import { DocumentDetailsInstructions } from "../../Instructions";
import { DocumentDetailsMetadata } from "../../Metadata";
import { DocumentDetailsRequiredBySection } from "../../RequiredBySection/Section";
import { DocumentPageScrollWrapper } from "../../ScrollWrapper";
import { DocumentDetailsStatusCard } from "../../StatusCard";
import { useDocumentDetailsActions } from "../../useDocumentDetailsActions";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";
import { useHelloSignFallbackUrl } from "../HelloSign/useHelloSignFallbackUrl";

export function DocumentDetailsCompositePage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const history = useHistory();
  const helloSignIframeContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const helloSignFormModalState = useModalState();
  const submittingDocumentModalState = useModalState();
  const { openModal: openSubmittingDocumentModal } = submittingDocumentModalState;
  const queryClient = useQueryClient();
  const { showInfoToast } = useToast();
  const shouldUseHelloSignFallbackUrl = useHelloSignFallbackUrl();

  const { requirement, status, requirementId } = workerRequirement;
  const { name, compositeConfig } = requirement?.attributes ?? {};
  const { requirements: compositeRequirements } = compositeConfig ?? {};
  const compositeHelloSignRequirement = compositeRequirements?.[0];
  const compositeDocumentUploadRequirement = compositeRequirements?.[1];
  const helloSignRequirementCtaText = compositeHelloSignRequirement?.ctaText;
  const documentUploadRequirementCtaText = compositeDocumentUploadRequirement?.ctaText;

  const { uploadDocument, deleteDocument } = useDocumentDetailsActions();

  const { data: helloSignRequirement, isLoading: isLoadingHelloSignRequirement } =
    useGetRequirements(
      {
        filter: {
          id: compositeHelloSignRequirement?.requirementId,
        },
      },
      {
        enabled: isDefined(compositeHelloSignRequirement?.requirementId),
      }
    );

  const helloSignConfig = helloSignRequirement?.data?.[0].attributes.helloSignConfig;
  const fallbackStaticUrl = helloSignConfig?.fallbackStaticUrl;
  const isEmbeddedFlowEnabled = helloSignConfig?.isEmbeddedFlowEnabled;

  const { isCreatingHelloSignSession, isSubmittingHelloSignDocument, startHelloSignEmbeddedFlow } =
    useHelloSignEmbeddedFlow({
      requirementId,
      compositeChildRequirementId: compositeHelloSignRequirement?.requirementId,
      onSuccess: (isDocumentApproved) => {
        const modalPath = isDocumentApproved
          ? DOCUMENT_SUBMISSION_APPROVED_PATH
          : DOCUMENT_SUBMISSION_IN_REVIEW_PATH;

        void invalidateWorkerRequirements(queryClient);

        submittingDocumentModalState.closeModal();
        helloSignFormModalState.closeModal();

        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${modalPath}`, {
            requirementId,
          })
        );
      },
      onError: () => {
        helloSignFormModalState.closeModal();
        submittingDocumentModalState.closeModal();
        history.replace(
          generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_ERROR_PATH}`, {
            requirementId,
          })
        );
      },
      helloSignIframeContainerRef,
      onHelloSignClientCancel: () => {
        helloSignFormModalState.closeModal();
      },
    });

  useEffect(() => {
    if (isSubmittingHelloSignDocument) {
      openSubmittingDocumentModal();
    }
  }, [isSubmittingHelloSignDocument, openSubmittingDocumentModal]);

  if (!isDefined(compositeHelloSignRequirement) || !isDefined(compositeDocumentUploadRequirement)) {
    return <DocumentsErrorPage title="Error loading document" />;
  }

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsMetadata />
        <DocumentDetailsStatusCard />
        <DocumentDetailsInstructions />
        <DocumentDetailsRequiredBySection />
      </DocumentPageScrollWrapper>

      <DocumentUploadFooter
        status={status}
        isLoading={isCreatingHelloSignSession || isLoadingHelloSignRequirement}
        signButtonText={helloSignRequirementCtaText}
        uploadButtonText={documentUploadRequirementCtaText}
        onSignButtonClick={async () => {
          if (
            isDefined(fallbackStaticUrl) &&
            (shouldUseHelloSignFallbackUrl || !isEmbeddedFlowEnabled)
          ) {
            const browserFinishedListener = await Browser.addListener(
              "browserFinished",
              async () => {
                showInfoToast(
                  "If you have signed this document, please check back here later for an update"
                );
                await browserFinishedListener.remove();
              }
            );
            await Browser.open({ url: fallbackStaticUrl });
          } else {
            helloSignFormModalState.openModal();
            void startHelloSignEmbeddedFlow();
          }
        }}
        onUploadButtonClick={uploadDocument}
        onDeleteButtonClick={deleteDocument}
      />

      <FullScreenDialog modalState={helloSignFormModalState}>
        <Box ref={helloSignIframeContainerRef} sx={{ height: "100%" }} />
      </FullScreenDialog>

      <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
    </PageWrapper>
  );
}
